import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { ChangeDetectionStrategy, Component, computed, inject, resource } from "@angular/core";
import { RouterModule } from "@angular/router";

import { HealthService } from "../../../../apis/networkdashboard/rest/services";
import { AliasEditorComponent } from "../../../../components/common/alias-editor/alias-editor.component";
import { WirelessKpiChart } from "../../../../components/dashboard/wireless-kpi-chart/wireless-kpi-chart.component";
import { KpiSubject } from "../../../../components/dashboard/wireless-kpi-chart/wireless-kpi-chart.helpers";
import { EMPTY_WIRELESS_KPIS } from "../../../../components/models/HealthIndicator";
import { ServiceId } from "../../../../components/subscription-detail/service-id/service-id";
import { SubscriptionDetailHeader } from "../../../../components/subscription-detail/subscription-detail-header/subscription-detail-header";
import { SubscriptionDatePipe } from "../../../../helpers/subscription-datepipe/subscription-datepipe";
import { WirelessSubscriptionDetails } from "../../types";
import { SubscriptionViewBase } from "../subscription-view-base";
import { AccessPointsSectionComponent } from "./components/access-points-section/access-points-section";
import { KpiSectionComponent } from "./components/kpi-section/kpi-section";
import { WirelessShortcutMenuComponent } from "./components/wireless-shortcut-menu/wireless-shortcut-menu";

const WIRELESS_CHART_CHOICES: { [key: string]: KpiSubject[] } = {
  AEROHIVE: [
    { key: "dhcp", label: "Kpi.Chart.Aerohive.dhcp" },
    { key: "voipup", label: "Kpi.Chart.Aerohive.voipup" },
    { key: "voipdown", label: "Kpi.Chart.Aerohive.voipdown" },
    { key: "throughputup", label: "Kpi.Chart.Aerohive.throughputup" },
    { key: "throughputdown", label: "Kpi.Chart.Aerohive.throughputdown" },
    { key: "radius", label: "Kpi.Chart.Aerohive.radius" },
  ],
  ARUBA: [
    { key: "wifi_assoc_time", label: "Kpi.Chart.Aruba.wifi_assoc_time" },
    { key: "dhcp_res_time", label: "Kpi.Chart.Aruba.dhcp_res_time" },
    { key: "dns_res_time", label: "Kpi.Chart.Aruba.dns_res_time" },
    { key: "radius", label: "Kpi.Chart.Aruba.radius" },
    { key: "voice_quality", label: "Kpi.Chart.Aruba.voice_quality" },
    { key: "throughput_up", label: "Kpi.Chart.Aruba.throughput_up" },
    { key: "throughput_down", label: "Kpi.Chart.Aruba.throughput_down" },
  ],
};

@Component({
  selector: "wireless-subscription-view",
  templateUrl: "wireless-subscription-view.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AccessPointsSectionComponent,
    AliasEditorComponent,
    AngularSvgIconModule,
    KpiSectionComponent,
    RouterModule,
    ServiceId,
    SubscriptionDatePipe,
    SubscriptionDetailHeader,
    TranslatePipe,
    WirelessKpiChart,
    WirelessShortcutMenuComponent,
  ],
})
export class WirelessSubscriptionViewComponent extends SubscriptionViewBase<WirelessSubscriptionDetails> {
  private healthService = inject(HealthService);

  protected supplier = computed(() => this.subscription().supplier);
  protected sensorPresent = computed(() => this.subscription().sensorPresent);
  protected accessPoints = computed(() => this.subscription().location?.accessPoints ?? []);
  protected address = computed(() => {
    const location = this.subscription().location;
    return location ? [location.address, location.city].filter(Boolean).join(", ") : "";
  });

  protected chartChoices = computed(() => WIRELESS_CHART_CHOICES[this.supplier()?.toUpperCase() ?? ""] ?? []);
  protected kpis = computed(() => this.wirelessServiceKpis.value());
  protected supportedBands = computed(() => {
    const { today, last3Days, lastWeek } = this.kpis();
    return (["twofour", "five", "six"] as const).filter(
      (band) => today[band] != null || last3Days[band] != null || lastWeek[band] != null,
    );
  });

  private wirelessServiceKpis = resource({
    params: () => ({ subscription_id: this.subscription().subscriptionId }),
    loader: ({ params }) =>
      this.healthService.getWirelessHealthDetail(params).catch((reason) => {
        console.warn("Failed to retrieve wireless health data:", reason);
        return EMPTY_WIRELESS_KPIS;
      }),
    defaultValue: EMPTY_WIRELESS_KPIS,
  });

  protected tickets = computed(() => this.subscription().tickets);
}
